import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Zap, Database, Phone, Megaphone, Share2, Bot, ArrowRight, Sparkles } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';

export default function Services() {
  useSEO({
    title: 'Services | Real-Time Leads, Live Transfers, Data & AI — Noax Media',
    description: 'Real-time US lead generation, live transfers, targeted data solutions, social media marketing, and AI automations for Medicare, Final Expense, insurance, and home services.',
    path: '/services',
  });
  const services = [
    {
      icon: Zap,
      title: 'Real-Time Leads',
      tagline: 'Fresh Intent, Delivered the Moment It Happens',
      description: 'Timing is everything. A lead that lands in your inbox seconds after they click submit is worth 10x more than one that has been sitting in a database for days. We deliver instantly from our creation platform.',
      benefits: [
        'Leads generated across multiple digital platforms',
        'Verified contact information and intent signals',
        'Instant delivery to your CRM, dialer, or inbox',
        'Full coverage across home and insurance verticals',
      ],
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&w=800&q=85',
    },
    {
      icon: Database,
      title: 'Data Solutions',
      tagline: 'The Right List Is Half the Battle',
      description: 'Not every campaign needs real-time leads. Sometimes you need clean, targeted, and compliant datasets built around your ideal customer profile.',
      benefits: [
        'Niche-specific, filtered data sets',
        'Compliance-conscious sourcing',
        'Segmentation built for your campaigns',
        'Scalable volume without sacrificing quality',
      ],
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?crop=entropy&cs=srgb&fm=jpg&w=800&q=85',
    },
    {
      icon: Phone,
      title: 'Live Transfers',
      tagline: 'Skip the Chase. Talk to Ready Buyers.',
      description: 'Why waste time calling cold contacts? With live transfers, you speak directly with pre-qualified, interested customers who are ready to have a conversation.',
      benefits: [
        'Real-time, phone-verified transfers',
        'Pre-screened, intent-qualified prospects',
        'Higher connect and conversion rates',
        'Available across all supported verticals',
      ],
      image: 'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?crop=entropy&cs=srgb&fm=jpg&w=800&q=85',
    },
    {
      icon: Megaphone,
      title: 'Social Media Marketing',
      tagline: 'Turn Attention Into Pipeline',
      description: 'We build and run paid social campaigns specifically designed to generate qualified demand. Not likes. Not vanity metrics. Leads. We manage the entire funnel.',
      benefits: [
        'Platform-specific campaign strategy',
        'High-converting ad creative and copy',
        'Precision audience targeting',
        'Continuous optimization and reporting',
      ],
      image: 'https://images.unsplash.com/photo-1611926653458-09294b3142bf?crop=entropy&cs=srgb&fm=jpg&w=800&q=85',
    },
    {
      icon: Share2,
      title: 'Social Media Management',
      tagline: 'A Brand People Trust Before They Buy',
      description: 'We manage your social channels end-to-end — building authority, growing your audience, and maintaining a presence that reinforces trust at every touchpoint.',
      benefits: [
        'Full content planning and posting',
        'Community engagement and management',
        'Consistent, on-brand messaging',
        'Steady audience and follower growth',
      ],
      image: 'https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7?crop=entropy&cs=srgb&fm=jpg&w=800&q=85',
    },
    {
      icon: Bot,
      title: 'AI Automations',
      tagline: 'Work Smarter, Close Faster',
      description: 'AI automations handle the grunt work — qualifying leads, routing them to the right person, and triggering follow-ups — so your team can focus on closing.',
      benefits: [
        'Automated lead qualification and scoring',
        'Smart routing to the right team member',
        'Instant follow-up sequences',
        'Custom workflows built around your process',
      ],
      image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?crop=entropy&cs=srgb&fm=jpg&w=800&q=85',
    },
  ];

  return (
    <div className="min-h-screen bg-[#0A1420] text-white overflow-hidden">
      {/* Animated Background */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#31638C] opacity-[0.04] rounded-full blur-3xl animate-drift"></div>
        <div className="absolute top-1/2 right-1/4 w-[500px] h-[500px] bg-[#4A8BC2] opacity-[0.03] rounded-full blur-3xl animate-float-slow"></div>
      </div>

      {/* Hero Section */}
      <section 
        className="relative pt-32 pb-20 lg:pt-36 lg:pb-24 grain-overlay"
        data-testid="services-hero"
      >
        <div className="absolute top-20 -left-20 w-96 h-96 bg-[#31638C] opacity-20 rounded-full blur-3xl animate-float-slow"></div>
        <div className="absolute top-40 -right-20 w-[500px] h-[500px] bg-[#4A8BC2] opacity-10 rounded-full blur-3xl animate-drift"></div>
        <div className="absolute inset-0 grid-pattern opacity-30"></div>

        <div className="relative max-w-5xl mx-auto px-6 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#31638C]/10 border border-[#31638C]/30 mb-6">
              <Sparkles className="w-4 h-4 text-[#31638C]" />
              <span className="text-xs uppercase tracking-widest text-[#31638C] font-medium">Our Services</span>
            </div>
            <h1 
              className="text-5xl lg:text-7xl font-bold tracking-tighter leading-none mb-6"
              data-testid="services-heading"
            >
              Lead Generation,<br />
              <span className="gradient-text">Engineered for Revenue</span>
            </h1>
            <p className="text-xl text-gray-400 leading-relaxed max-w-3xl mx-auto">
              From the first click to the closed deal, Noax Media builds the full engine that keeps 
              your sales team busy — and your revenue climbing.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services List */}
      <section className="relative py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 space-y-32">
          {services.map((service, index) => {
            const Icon = service.icon;
            const isEven = index % 2 === 0;

            return (
              <motion.div
                key={index}
                className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.8 }}
                data-testid={`service-${index}`}
              >
                <div className={`lg:col-span-6 ${!isEven ? 'lg:order-2' : ''}`}>
                  <div className="relative group">
                    <div className="absolute -inset-4 bg-gradient-to-r from-[#31638C]/20 to-[#4A8BC2]/20 rounded-2xl blur-2xl group-hover:opacity-70 transition-opacity"></div>
                    <div className="relative rounded-2xl overflow-hidden border border-[#31638C]/20">
                      <img
                        src={service.image}
                        alt={`${service.title} — Noax Media real-time US lead generation for Medicare leads, final expense leads, and home services`}
                        className="w-full h-80 object-cover group-hover:scale-105 transition-transform duration-700"
                        style={{ filter: 'brightness(0.55) contrast(1.1) saturate(0.7)' }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-tr from-[#0A1420] via-[#0A1420]/40 to-transparent"></div>
                      <div className="absolute top-6 left-6 flex items-center gap-3">
                        <div className="w-14 h-14 rounded-xl bg-[#0A1420]/90 backdrop-blur-xl border border-[#31638C]/30 flex items-center justify-center">
                          <Icon className="text-[#31638C]" size={26} />
                        </div>
                        <div className="text-4xl font-bold text-white/20">
                          0{index + 1}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className={`lg:col-span-6 ${!isEven ? 'lg:order-1' : ''}`}>
                  <h2 
                    className="text-3xl lg:text-5xl font-bold tracking-tight mb-3 text-white"
                  >
                    {service.title}
                  </h2>
                  <p className="text-[#31638C] font-medium text-lg mb-6">
                    {service.tagline}
                  </p>
                  <p className="text-gray-400 leading-relaxed mb-8 text-lg">
                    {service.description}
                  </p>
                  <div className="space-y-3">
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#31638C] mb-4">
                      What you get
                    </p>
                    {service.benefits.map((benefit, benefitIndex) => (
                      <motion.div 
                        key={benefitIndex} 
                        className="flex items-start group"
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: benefitIndex * 0.1 }}
                        data-testid={`service-${index}-benefit-${benefitIndex}`}
                      >
                        <div className="w-2 h-2 bg-[#4A8BC2] rounded-full mt-2 mr-3 flex-shrink-0 group-hover:scale-150 transition-transform"></div>
                        <span className="text-gray-300 group-hover:text-white transition-colors">{benefit}</span>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-24 lg:py-32 overflow-hidden mt-16" data-testid="services-cta">
        <div className="absolute inset-0 bg-gradient-to-br from-[#31638C] via-[#31638C] to-[#1F4266] animate-gradient"></div>
        <div className="absolute inset-0 grid-pattern opacity-20"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#131E2C] opacity-10 rounded-full blur-3xl animate-drift"></div>

        <div className="relative max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 
              className="text-4xl lg:text-6xl font-bold tracking-tight mb-6 text-white leading-tight"
            >
              One Partner.<br />
              Every Lead-Gen Need Covered.
            </h2>
            <p className="text-xl text-gray-300 leading-relaxed mb-10 max-w-2xl mx-auto">
              Whether you need steady leads, smarter follow-up, or a complete demand engine, 
              Noax Media builds a solution tailored to your goals.
            </p>
            <Link
              to="/about#contact"
              className="group inline-flex items-center px-10 py-4 bg-[#131E2C] text-[#31638C] font-semibold rounded-full hover:-translate-y-1 hover:shadow-2xl transition-all duration-300"
              data-testid="services-cta-button"
            >
              Request a Custom Plan
              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
